import axios from 'axios'
import router from '../router'
import { defineStore } from 'pinia'
import { primeCsrfCookie } from '@/lib/http'
import type { User } from '@/types/User'
import type { APIResponse } from '@/types/Response'

/**
 * Session state for Sanctum's SPA (cookie) authentication.
 *
 * The credential itself is an HttpOnly session cookie the browser holds and this
 * code cannot read, so there is nothing here but the signed-in user. `persist`
 * keeps that user across reloads purely so the app can render immediately;
 * `restoreSession()` re-confirms it against the API on every boot, and the
 * cookie — not this state — is what actually authorises a request.
 */
export const useAuthStore = defineStore({
    id: 'auth',
    persist: true,
    state: () => ({
        user_data: null as User | null
    }),
    getters: {
        user: (state) => state.user_data as User | null
    },
    actions: {
        /**
         * Signs in against the session guard.
         *
         * The CSRF cookie has to exist before the POST, so it is primed first.
         * Rejections carry the axios error through untouched, which is what the
         * login form reads validation messages from.
         */
        async login(payload: any): Promise<APIResponse> {
            await primeCsrfCookie()

            const response = await axios.post('/login', payload)
            this.user_data = response.data.data.user
            router.push('/app/dashboard')

            return response.data
        },

        async register(payload: any): Promise<APIResponse> {
            await primeCsrfCookie()

            const response = await axios.post('/register', payload)
            this.user_data = response.data.data.user
            // Was '/admin/dashboard', which has never been a route in this app.
            router.push('/app/dashboard')

            return response.data
        },

        async profile(): Promise<APIResponse> {
            const response = await axios.get('/auth/profile')
            /*
             * The user sits at `data.data`, the same as every other endpoint.
             *
             * This used to read `data.data.data`, from when the endpoint nested
             * one level deeper. The API has since been reconciled, so that path
             * resolved to undefined and wiped the signed-in user on every hard
             * navigation or reload — taking `gym_id` with it, which is what
             * every gym-scoped query keys on.
             */
            this.user_data = response.data.data

            return response.data
        },

        async logout(): Promise<APIResponse> {
            try {
                const response = await axios.post('/logout')
                return response.data
            } finally {
                // Clear locally even if the request failed. A user who asked to
                // sign out should not stay signed in because the network blipped.
                this.clearSession()
            }
        },

        /**
         * Called by the response interceptor when the session is gone.
         *
         * Only a page that needs a session sends the visitor to /login. Every
         * fresh load asks the API who is signed in, and a signed-out visitor
         * gets a 401 on every one; redirecting regardless bounced the emailed
         * reset link back to sign-in and swallowed the social callback's error.
         *
         * Waits for the first navigation, because on a fresh load the 401 can
         * arrive before the router knows which page it is on.
         */
        async forceLogout() {
            this.user_data = null

            await router.isReady()
            const route = router.currentRoute.value
            if (route.matched.some((record) => record.meta.requiresAuth)) {
                router.push('/login')
            }
        },

        clearSession() {
            this.user_data = null

            // Guarded so a 401 arriving while already on the login screen — a
            // failed sign-in, say — doesn't push a redundant navigation.
            if (router.currentRoute.value.path !== '/login') {
                router.push('/login')
            }
        },

        async updateProfile(payload: any): Promise<APIResponse> {
            const response = await axios.put('/auth/profile', payload)
            this.user_data = response.data.data

            return response.data
        },

        async updateSettings(payload: any): Promise<APIResponse> {
            const response = await axios.post('/auth/settings', payload)
            this.user_data = response.data.data

            return response.data
        },

        async updatePassword(payload: any): Promise<APIResponse> {
            const response = await axios.put('/auth/password', payload)
            this.user_data = response.data.data

            return response.data
        }
    }
})
